import { Alert, Button, Card, Form, Input, InputNumber, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationCampusSelect } from '@/pages/education/components/EducationLookupSelects';

type Room = {
  id: string;
  campus_id?: string | null;
  code: string;
  name: string;
  capacity?: number | null;
  is_active?: number | boolean;
};

type FormValues = {
  campus_id?: string;
  code: string;
  name: string;
  capacity?: number | null;
  is_active?: boolean;
};

export default function TimetableRoomFormPage() {
  const { t } = useTranslation('education');
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.timetable.manage') || user?.permissions?.includes('education.admin')
  );
  const isNew = !id || id === 'new';
  const [form] = Form.useForm<FormValues>();

  const list = useQuery({
    queryKey: ['education-timetable-rooms'],
    enabled: !isNew && Boolean(id),
    queryFn: async () => {
      const res = await api.get<Room[]>('/education/timetable/rooms');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  useEffect(() => {
    if (isNew) {
      form.setFieldsValue({ is_active: true });
      return;
    }
    const row = (list.data ?? []).find((r) => r.id === id);
    if (!row) return;
    form.setFieldsValue({
      campus_id: row.campus_id || undefined,
      code: row.code,
      name: row.name,
      capacity: row.capacity ?? undefined,
      is_active: !(row.is_active === 0 || row.is_active === false),
    });
  }, [form, id, isNew, list.data]);

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      isNew ? api.post('/education/timetable/rooms', values) : api.put(`/education/timetable/rooms/${id}`, values),
    onSuccess: () => {
      message.success(isNew ? t('roomCreated') : t('roomUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-timetable-rooms'] });
      navigate('/education/timetable/rooms');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (!canManage) {
    return (
      <EducationPageChrome title={isNew ? t('newRoom') : t('editRoom')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={isNew ? t('newRoom') : t('editRoom')}
      description={t('timetableRoomsIntro')}
      extra={<Button onClick={() => navigate('/education/timetable/rooms')}>{t('backToRooms')}</Button>}
    >
      <Card size="small" style={{ maxWidth: 640 }}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Form.Item name="code" label={t('code')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="campus_id" label={t('campus')}>
            <EducationCampusSelect style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item name="capacity" label={t('capacity')}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          {!isNew ? (
            <Form.Item name="is_active" label={t('status')} rules={[{ required: true }]}>
              <Select
                options={[
                  { value: true, label: t('active') },
                  { value: false, label: t('inactive') },
                ]}
              />
            </Form.Item>
          ) : null}
          <Space>
            <Button type="primary" htmlType="submit" loading={save.isPending}>
              {t('save')}
            </Button>
            <Button onClick={() => navigate('/education/timetable/rooms')}>{t('cancel')}</Button>
          </Space>
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
